import { useEffect, useMemo, useState, useRef } from "react";
import { Link, useParams, useNavigate, Navigate } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowLeft, ArrowUpRight, ChevronRight } from "lucide-react";
import { SECTORS, getSectorBySlug } from "@/data/sectors";
import Navbar from "@/components/site/Navbar";
import Footer from "@/components/site/Footer";
import BackgroundFX from "@/components/site/BackgroundFX";

const slugify = (s) =>
  s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");

const logoCandidates = (domain) => [
  `https://logo.clearbit.com/${domain}`,
  `https://unavatar.io/${domain}?fallback=false`,
  `https://www.google.com/s2/favicons?domain=${domain}&sz=128`,
];

export const PortfolioSector = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const sector = useMemo(() => getSectorBySlug(slug), [slug]);

  const containerRef = useRef(null);
  const [active, setActive] = useState(0);

  useEffect(() => {
    if (!sector || sector.companies.length === 0) return;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const idx = Number(entry.target.getAttribute("data-idx"));
            setActive(idx);
          }
        });
      },
      { threshold: 0.55 }
    );
    const nodes = containerRef.current?.querySelectorAll("[data-company-panel]");
    nodes?.forEach((n) => observer.observe(n));
    return () => observer.disconnect();
  }, [sector]);

  if (!sector) return <Navigate to="/portfolio" replace />;

  const Icon = sector.Icon;
  const hasCompanies = sector.companies.length > 0;

  return (
    <main className="relative bg-[#F7F6F3] text-[#1A1A1A] overflow-x-hidden">
      <BackgroundFX />
      <div className="relative z-10">
        <Navbar />

        {/* === TOP: Sector switcher (was at the bottom previously) === */}
        <section className="relative pt-32 pb-8">
          <div className="max-w-7xl mx-auto px-6 lg:px-10">
            <div className="flex items-center justify-between flex-wrap gap-4 mb-5">
              <button
                onClick={() => navigate(-1)}
                data-testid="sector-back-button"
                className="group inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] text-[#5C5A55] hover:text-[#136207] transition-colors"
              >
                <ArrowLeft className="w-3.5 h-3.5 group-hover:-translate-x-0.5 transition-transform" />
                Back
              </button>
              <span className="overline text-[#136207]">Sectors</span>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-px bg-[#E0DDD6] border border-[#E0DDD6] backdrop-blur-sm">
              {SECTORS.map((s) => {
                const SectorIcon = s.Icon;
                const isActive = s.slug === sector.slug;
                return (
                  <Link
                    key={s.slug}
                    to={`/portfolio/${s.slug}`}
                    data-testid={`sector-switch-${s.slug}`}
                    className={`group relative flex flex-col items-center justify-center text-center px-3 py-5 backdrop-blur transition-colors ${
                      isActive
                        ? "bg-[#136207] text-white"
                        : "bg-[#FFFFFF]/70 text-[#1A1A1A] hover:bg-[#EFEDE8]/90"
                    }`}
                  >
                    <div
                      className={`w-7 h-7 mb-2 ${isActive ? "text-white" : "text-[#136207] opacity-90 group-hover:opacity-100"} transition`}
                    >
                      <SectorIcon className="w-full h-full" />
                    </div>
                    <span
                      className={`font-display text-base sm:text-lg tracking-tight leading-none ${
                        isActive ? "text-white" : "text-[#1A1A1A]"
                      }`}
                    >
                      {s.name}
                    </span>
                    <span
                      className={`mt-1 text-[9px] uppercase tracking-[0.22em] ${
                        isActive ? "text-[#5C5A55]" : "text-[#8A8880]"
                      }`}
                    >
                      {s.companies.length > 0
                        ? `${s.companies.length} co.`
                        : "Soon"}
                    </span>
                  </Link>
                );
              })}
            </div>
          </div>
        </section>

        {/* Sector header */}
        <section className="relative pt-8 pb-16 lg:pb-24 border-b border-[#E0DDD6]">
          <div
            aria-hidden
            className="absolute -top-20 right-[-10%] w-[60vw] h-[60vw] max-w-[900px] max-h-[900px] rounded-full pointer-events-none"
            style={{
              background:
                "radial-gradient(circle at center, rgba(19,98,7,0.10) 0%, rgba(19,98,7,0) 60%)",
              filter: "blur(50px)",
            }}
          />
          <div className="max-w-7xl mx-auto px-6 lg:px-10 relative">
            <div className="mt-4 grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-16 items-end">
              <div className="lg:col-span-7">
                <div className="flex items-center gap-3 mb-6">
                  <span className="inline-block w-8 h-px bg-[#136207]" />
                  <span className="overline text-[#136207]">
                    Sector · {sector.code} · {sector.companies.length}{" "}
                    {sector.companies.length === 1 ? "company" : "companies"}
                  </span>
                </div>
                <div className="flex items-end gap-6">
                  <div
                    className="w-20 h-20 shrink-0 border border-[#136207]/40 bg-[#FFFFFF]/70 backdrop-blur flex items-center justify-center"
                    style={{ boxShadow: "inset 0 0 30px rgba(19,98,7,0.18)" }}
                  >
                    <div
                      className="w-12 h-12 text-[#136207]"
                    >
                      <Icon className="w-full h-full" />
                    </div>
                  </div>
                  <h1 className="font-display text-[#1A1A1A] text-5xl sm:text-6xl lg:text-7xl tracking-tight leading-none">
                    {sector.name}
                  </h1>
                </div>
              </div>
              <p className="lg:col-span-5 text-[#5C5A55] text-base sm:text-lg leading-relaxed font-light">
                {sector.description}
              </p>
            </div>
          </div>
        </section>

        {/* Body */}
        {hasCompanies ? (
          <section className="relative py-16 lg:py-24" ref={containerRef}>
            <div className="max-w-7xl mx-auto px-6 lg:px-10">
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
                {/* Left rail */}
                <aside className="lg:col-span-4">
                  <div className="lg:sticky lg:top-32 space-y-8">
                    <div>
                      <div className="overline mb-3">Active</div>
                      <div className="font-display text-[#1A1A1A] text-3xl tracking-tight leading-tight">
                        {sector.companies[active]?.confidential
                          ? `Miner ${active + 1}`
                          : sector.companies[active]?.name}
                      </div>
                      <div className="mt-1 text-[#136207] text-sm tracking-wider">
                        {String(active + 1).padStart(2, "0")} /{" "}
                        {String(sector.companies.length).padStart(2, "0")}
                      </div>
                    </div>

                    <ul className="space-y-3" data-testid="sector-company-list">
                      {sector.companies.map((c, i) => {
                        const label = c.confidential ? `Miner ${i + 1}` : c.name;
                        const testKey = c.confidential ? `miner-${i + 1}` : slugify(c.name);
                        return (
                          <li key={testKey}>
                            <a
                              href={`#c-${i}`}
                              data-testid={`sector-list-${testKey}`}
                              className={`group flex items-center gap-3 py-3 border-t border-[#E0DDD6] transition-colors ${
                                active === i
                                  ? "text-[#1A1A1A]"
                                  : "text-[#5C5A55] hover:text-[#1A1A1A]"
                              }`}
                            >
                              <span
                                className="w-1 h-6 transition-all"
                                style={{
                                  background: active === i ? "#136207" : "rgba(255,255,255,0.12)",
                                  boxShadow: active === i ? "0 0 12px rgba(19,98,7,0.7)" : "none",
                                }}
                              />
                              <span className="text-[11px] uppercase tracking-[0.22em] shrink-0 w-7">
                                {String(i + 1).padStart(2, "0")}
                              </span>
                              <span className="font-display text-lg tracking-tight">
                                {label}
                              </span>
                              <ChevronRight className="ml-auto w-3.5 h-3.5 opacity-0 group-hover:opacity-60 transition" />
                            </a>
                          </li>
                        );
                      })}
                    </ul>

                    <div className="pt-6 border-t border-[#E0DDD6]">
                      <div className="overline mb-3">Thesis</div>
                      <p className="text-[#5C5A55] text-sm leading-relaxed font-light">
                        {sector.tagline}
                      </p>
                    </div>
                  </div>
                </aside>

                {/* Right — company panels (compact grid) */}
                <div className="lg:col-span-8 grid grid-cols-1 xl:grid-cols-2 gap-4">
                  {sector.companies.map((c, i) => (
                    <CompanyPanel
                      key={c.confidential ? `miner-${i}` : c.name}
                      company={c}
                      idx={i}
                      total={sector.companies.length}
                      sectorName={sector.name}
                    />
                  ))}
                </div>
              </div>
            </div>
          </section>
        ) : (
          <EmptySector sector={sector} />
        )}

        <Footer />
      </div>
    </main>
  );
};

const CompanyLogo = ({ company }) => {
  const candidates = logoCandidates(company.domain);
  const [idx, setIdx] = useState(0);
  const [failed, setFailed] = useState(false);
  if (failed) {
    return (
      <div className="w-12 h-12 sm:w-16 sm:h-16 shrink-0 border border-[#E0DDD6] bg-[#FFFFFF]/70 backdrop-blur flex items-center justify-center font-display text-[#1A1A1A] text-2xl">
        {company.name.slice(0, 2)}
      </div>
    );
  }
  return (
    <div
      className="w-12 h-12 sm:w-16 sm:h-16 shrink-0 border border-[#E0DDD6] bg-[#FFFFFF]/70 backdrop-blur flex items-center justify-center overflow-hidden"
      style={{ boxShadow: "inset 0 0 24px rgba(19,98,7,0.08)" }}
    >
      <img
        src={candidates[idx]}
        alt={`${company.name} logo`}
        loading="lazy"
        referrerPolicy="no-referrer"
        onError={() => {
          if (idx + 1 < candidates.length) setIdx(idx + 1);
          else setFailed(true);
        }}
        className="max-w-[80%] max-h-[80%] object-contain"
      />
    </div>
  );
};

const CompanyPanel = ({ company, idx, total, sectorName }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });
  const y = useTransform(scrollYProgress, [0, 1], [40, -40]);

  return (
    <motion.article
      ref={ref}
      id={`c-${idx}`}
      data-company-panel
      data-idx={idx}
      data-testid={company.confidential ? `sector-company-miner-${idx + 1}` : `sector-company-${slugify(company.name)}`}
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-100px" }}
      transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
      className="relative bg-[#FFFFFF]/70 backdrop-blur-sm border border-[#E0DDD6] overflow-hidden hover:border-[#136207]/40 transition-colors"
    >
      <motion.span
        aria-hidden
        style={{ y }}
        className="absolute -right-24 -top-24 w-72 h-72 rounded-full pointer-events-none"
      >
        <span
          className="block w-full h-full"
          style={{
            background:
              "radial-gradient(circle at center, rgba(19,98,7,0.14) 0%, rgba(19,98,7,0) 70%)",
            filter: "blur(30px)",
          }}
        />
      </motion.span>

      <div className="relative p-6 lg:p-7 flex flex-col h-full">
        {/* Top row: logo (or crest) + index */}
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            {company.confidential ? (
              <div
                className="w-12 h-12 sm:w-16 sm:h-16 shrink-0 border border-[#136207]/30 bg-[#FFFFFF]/70 backdrop-blur flex items-center justify-center font-display text-[#136207] text-xl tracking-tight"
                aria-hidden
              >
                {String(idx + 1).padStart(2, "0")}
              </div>
            ) : (
              <CompanyLogo company={company} />
            )}
            <span className="overline">{sectorName}</span>
          </div>
          <span className="font-display text-[#136207] text-2xl leading-none">
            {String(idx + 1).padStart(2, "0")}
            <span className="text-[#8A8880] text-sm">/{String(total).padStart(2, "0")}</span>
          </span>
        </div>

        {/* Body */}
        <div className="mt-5 flex-1">
          {company.confidential ? (
            <h2 className="font-display text-[#1A1A1A] text-2xl sm:text-[28px] tracking-tight leading-[1.1] break-words">
              Miner {idx + 1}
            </h2>
          ) : (
            <div className="flex items-start justify-between gap-3">
              <h2 className="font-display text-[#1A1A1A] text-2xl sm:text-[28px] tracking-tight leading-[1.1] break-words">
                {company.name}
              </h2>
              <a
                href={`https://${company.domain}`}
                target="_blank"
                rel="noreferrer"
                className="shrink-0 w-9 h-9 border border-[#E0DDD6] hover:border-[#136207] hover:text-[#136207] text-[#5C5A55] transition-colors flex items-center justify-center"
                aria-label={`Visit ${company.name}`}
              >
                <ArrowUpRight className="w-4 h-4" />
              </a>
            </div>
          )}

          <p className="mt-3 text-[#5C5A55] text-sm leading-relaxed font-light">
            {company.note}
          </p>
        </div>

        <div className="mt-5 flex items-center gap-3 pt-4 border-t border-[#E0DDD6]">
          <span className="overline">Private investment</span>
          {!company.confidential && (
            <>
              <span className="text-[#8A8880]">·</span>
              <span className="overline text-[#8A8880] truncate">{company.domain}</span>
            </>
          )}
        </div>
      </div>
    </motion.article>
  );
};

const EmptySector = ({ sector }) => (
  <section className="relative py-24 lg:py-32">
    <div className="max-w-4xl mx-auto px-6 lg:px-10 text-center">
      <div
        aria-hidden
        className="mx-auto w-24 h-24 border border-[#136207]/30 bg-[#FFFFFF]/70 backdrop-blur flex items-center justify-center mb-8"
        style={{ boxShadow: "inset 0 0 40px rgba(19,98,7,0.2)" }}
      >
        <div
          className="w-14 h-14 text-[#136207]"
        >
          <sector.Icon className="w-full h-full" />
        </div>
      </div>
      <h2 className="font-display text-[#1A1A1A] text-4xl sm:text-5xl tracking-tight">
        Companies <span className="text-[#136207]">to be announced.</span>
      </h2>
      <p className="mt-6 text-[#5C5A55] text-base sm:text-lg leading-relaxed font-light max-w-2xl mx-auto">
        Active capital is being deployed in {sector.name.toLowerCase()}. Portfolio
        positions will be announced as they reach the appropriate stage for public
        disclosure.
      </p>
      <div className="mt-12 inline-flex items-center gap-3 px-6 py-4 border border-[#E0DDD6]">
        <span className="w-2 h-2 bg-[#136207] animate-pulse" />
        <span className="overline">Capital deployed · Disclosure pending</span>
      </div>
    </div>
  </section>
);

export default PortfolioSector;
